import { AsyncPipe, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  Inject,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { BehaviorSubject, of, zip } from 'rxjs';
import { first } from 'rxjs/operators';

import { appInject } from 'src/app/app-injector';
import { AppErrorMajor } from 'src/app/core/app-error/app-error-major.enum';
import { AppError } from 'src/app/core/app-error/app-error.interface';
import { AppErrorService } from 'src/app/core/app-error/app-error.service';
import { Theme } from 'src/app/core/services/theme/theme.enum';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { TrackingPlaceholder } from 'src/app/core/services/tracking/models/tracking-placeholder.enum';
import { TrackingService } from 'src/app/core/services/tracking/tracking.service';
import { AppRoutePathParam } from 'src/app/routing/app-route-path-param.enum';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { fromCarl } from 'src/app/store/common/common.selectors';
import { selectRouteState } from 'src/app/store/router/router.selectors';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { ERROR_MISSING_VEHICLE_CLASS_REDIRECT_PATH } from 'src/app/utils/global-constants';
import { WINDOW } from 'src/app/utils/window.token';

import { LoadingSpinnerComponent } from '../../../shared/loading-spinner/loading-spinner.component';
import { ModalHostComponent } from '../../../shared/modal/modal-host/modal-host.component';
import { URLS } from '../../config/url.config';

import { errorPageFallbackTexts } from './error-page.fallback-texts';
import { ResolveBaumusterComponent } from './error-types/resolve-baumuster/resolve-baumuster.component';

const DEFAULT_HOME_URL = URLS.misc.modelSelection;

@Component({
  selector: 'cc-error-page',
  templateUrl: './error-page.component.html',
  styleUrl: './error-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    ResolveBaumusterComponent,
    LoadingSpinnerComponent,
    ModalHostComponent,
    AsyncPipe
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ErrorPageComponent implements OnInit {
  public readonly loadingSpinnerVisibleSubject = new BehaviorSubject<boolean>(false);
  public readonly loadingSpinnerVisible$ = this.loadingSpinnerVisibleSubject.asObservable();
  public currentErrorMajorType!: AppErrorMajor;
  public headline!: string;
  public description!: string;
  public primaryAction!: string;
  public homeUrl$ = this.store.select(fromCarl.selectConfig('header.url'));

  public readonly defaultHomeUrl = DEFAULT_HOME_URL;

  public constructor(
    private readonly themeService: ThemeService,
    private readonly appErrorService: AppErrorService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly trackingService: TrackingService,
    private readonly store: Store,
    @Inject(WINDOW) public readonly window: Window
  ) {}

  public ngOnInit(): void {
    this.themeService.setTheme(Theme.Dark);

    this.appErrorService.currentError$
      .pipe(filterTruthy(), first(), takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe((currentError: AppError) => {
        this.currentErrorMajorType = currentError.type as AppErrorMajor;
        this.changeDetectorRef.markForCheck();

        this.generateTranslationStrings();

        if (!this.currentErrorMajorType) {
          // Data is only sent if there is no error, because page was reloaded or directly accessed
          this.trackingService.sendTrackingData({
            routeState: AppRoutePath.ErrorPage,
            errorData: {
              errorType: TrackingPlaceholder.ErrorTypeUndefined,
              errorDescription: TrackingPlaceholder.ErrorDescriptionUndefined
            }
          });
        }
      });

    this.redirectIfVehicleClassIsUnknown();
  }

  public showLoadingSpinner(show: boolean): void {
    this.loadingSpinnerVisibleSubject.next(show);
  }

  private generateTranslationStrings(): void {
    if (!this.currentErrorMajorType) {
      return;
    }
    if (this.currentErrorMajorType === errorPageFallbackTexts.key) {
      this.homeUrl$ = of(this.defaultHomeUrl);
      this.changeDetectorRef.markForCheck();

      this.setTranslationStrings(
        errorPageFallbackTexts.headline,
        errorPageFallbackTexts.description,
        errorPageFallbackTexts.primaryAction
      );
      return;
    }
    zip(
      this.store.select(fromCarl.selectTranslation(`error.major.${this.currentErrorMajorType}.headline`)),
      this.store.select(fromCarl.selectTranslation(`error.major.${this.currentErrorMajorType}.description`)),
      this.store.select(fromCarl.selectTranslation(`error.major.${this.currentErrorMajorType}.primaryAction`))
    )
      .pipe(takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe((texts: [string, string, string]) => {
        this.setTranslationStrings(...texts);
      });
  }

  private setTranslationStrings(headline: string, description: string, primaryAction: string): void {
    this.headline = headline;
    this.description = description;
    this.primaryAction = primaryAction;
    this.changeDetectorRef.markForCheck();
  }

  private redirectIfVehicleClassIsUnknown(): void {
    this.store
      .select(selectRouteState)
      .pipe(filterTruthy(), first(), takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(routeState => {
        const isValidRoute =
          routeState?.appRoutePath === AppRoutePath.Configuration ||
          routeState?.appRoutePath === AppRoutePath.Motorization ||
          routeState?.appRoutePath === AppRoutePath.VehicleLength ||
          routeState?.appRoutePath === AppRoutePath.Preconfig;
        if (isValidRoute && !routeState.pathParams[AppRoutePathParam.VehicleClass]) {
          // The protocol is intentionally missing to support both http and https, the browser automatically uses the currently in-use protocol
          this.window.location.href = `//${
            this.window.location.host
          }/${ERROR_MISSING_VEHICLE_CLASS_REDIRECT_PATH.replace(/^[/\\]+/, '')}`;
        }
      });
  }
}
